import { memo, useCallback, useMemo } from 'react';
import { CustomExtendedDataField, FieldSelect, FieldTextInput } from '../../../../components';
import AgencySelect from '../../../../components/CustomExtendedDataField/AgencySelect';
import { divisionOptions, divisionSchoolOptions, US_STATES } from '../../../../config/configExtras';
import { TalentLevel, TalentType, UserType } from '../../../../util/enums';
import { required } from '../../../../util/validators';
import css from './EditListingDetailsForm.module.css';
import { getConferences, getSchools } from '../../../../util/editListingHelpers';

const SCHOOL_FIELDS = ['division', 'conference', 'school'];

const RenderLocationSections = memo(
  ({ locationSectionsFieldProps, intl, currentStep, formId, values, form, userType, FINAL }) => {
    const { pub_talent_level, pub_talent_type } = values || {};

    const isEligibleForSchoolAndConference = useMemo(
      () =>
        pub_talent_level === TalentLevel.COLLEGIATE || pub_talent_level === TalentLevel.HIGH_SCHOOL,
      [pub_talent_level]
    );

    const getSchoolDetails = useCallback(school => {
      const selectedSchool = divisionSchoolOptions.find(s => s.school === school);
      return selectedSchool
        ? { city: selectedSchool.city || '', state: selectedSchool.state || '' }
        : { city: '', state: '' };
    }, []);

    const handleSchoolChange = useCallback(
      value => {
        form.change('school', value);
        const { city, state } = getSchoolDetails(value);
        form.batch(() => {
          form.change('currentCity', city);
          form.change('city', city);
          form.change('state', state);
          form.change('currentState', state);
        });
      },
      [form, getSchoolDetails]
    );

    const stateOptions = useMemo(
      () => (
        <>
          <option disabled value="">
            {intl.formatMessage({ id: 'EditListingDetailsForm.stateOption' })}
          </option>
          {US_STATES.map((state, index) => (
            <option key={index} value={state.value}>
              {state.label}
            </option>
          ))}
        </>
      ),
      [intl]
    );

    const renderCityStateFields = useCallback(
      ({
        cityId,
        stateId,
        cityName,
        stateName,
        cityLabelId,
        stateLabelId,
        cityValue,
        stateValue,
        isReadOnly = false,
      }) => (
        <div className={css.inlineFields}>
          <div className={css.inlineField}>
            {isReadOnly ? (
              <div className={css.inlineField}>
                <label htmlFor={cityId}>{intl.formatMessage({ id: cityLabelId })}</label>
                <input
                  id={cityId}
                  name={cityName}
                  type="text"
                  className={css.customField}
                  value={cityValue || ''}
                  onChange={e => form.change(cityName, e.target.value)}
                />
              </div>
            ) : (
              <FieldTextInput
                id={cityId}
                name={cityName}
                type="text"
                className={css.customField}
                label={intl.formatMessage({ id: cityLabelId })}
                placeholder={intl.formatMessage({ id: 'EditListingDetailsForm.cityPlaceholder' })}
              />
            )}
          </div>
          <div className={css.inlineField}>
            <FieldSelect
              id={stateId}
              name={stateName}
              className={css.customField}
              label={intl.formatMessage({ id: stateLabelId })}
              value={stateValue || ''}
              onChange={value => form.change(stateName, value)}
            >
              {stateOptions}
            </FieldSelect>
          </div>
        </div>
      ),
      [intl, form, stateOptions, css]
    );

    const renderAgencyField = useCallback(
      () => (
        <div className={css.hometownSection}>
          <div className={css.inlineFields}>
            <div className={css.inlineField}>
              <AgencySelect
                name="pub_agency"
                validate={required(
                  intl.formatMessage({ id: 'RenderLocationSections.agencySelect' })
                )}
                values={values}
                formId={formId}
                intl={intl}
              />
            </div>
          </div>
        </div>
      ),
      [intl, values, formId, css]
    );

    const renderSchoolFields = useCallback(
      () => (
        <>
          <FieldSelect
            className={css.customField}
            name="division"
            id="division"
            label={intl.formatMessage({ id: 'EditListingDetailsForm.divisionLabel' })}
            onChange={value => form.change('division', value)}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'EditListingDetailsForm.divisionOption' })}
            </option>
            {divisionOptions.map((o, index) => (
              <option key={index} value={o.key}>
                {o.value}
              </option>
            ))}
          </FieldSelect>

          {values?.division && (
            <FieldSelect
              className={css.customField}
              name="conference"
              id="conference"
              label={intl.formatMessage({ id: 'EditListingDetailsForm.conferenceLabel' })}
              onChange={value => form.change('conference', value)}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'EditListingDetailsForm.conferenceOption' })}
              </option>
              {getConferences(values.division).map((conference, index) => (
                <option key={index} value={conference}>
                  {conference}
                </option>
              ))}
            </FieldSelect>
          )}

          {values?.division && values?.conference && (
            <FieldSelect
              className={css.customField}
              name="school"
              id="school"
              label={intl.formatMessage({ id: 'EditListingDetailsForm.schoolLabel' })}
              onChange={handleSchoolChange}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'EditListingDetailsForm.schoolOption' })}
              </option>
              {getSchools(values.division, values.conference).map((school, index) => (
                <option key={index} value={school.school}>
                  {school.school}
                </option>
              ))}
            </FieldSelect>
          )}
        </>
      ),
      [intl, form, values?.division, values?.conference, handleSchoolChange]
    );

    const isNotHighSchool = pub_talent_level !== TalentLevel.HIGH_SCHOOL;
    const isCollegiateAthlete =
      pub_talent_level === TalentLevel.COLLEGIATE && pub_talent_type === TalentType.ATHLETE;
    const showSchoolFields = isNotHighSchool || isCollegiateAthlete;

    return (
      <div className={css.renderLocationSections}>
        <h1>{intl.formatMessage({ id: 'EditListingDetailsForm.locationInfo' })}</h1>
        <div className={css.customFields}>
          {isEligibleForSchoolAndConference ? (
            userType === UserType.TALENT &&
            currentStep === FINAL && (
              <>
                {showSchoolFields && renderSchoolFields()}

                {(values?.pub_talent_level === TalentLevel.COLLEGIATE ||
                  values?.pub_talent_level === TalentLevel.HIGH_SCHOOL) && (
                  <>
                    {values?.school && (
                      <div className={css.manualLocationFields}>
                        {renderCityStateFields({
                          cityId: 'currentCity',
                          stateId: 'currentState',
                          cityName: 'currentCity',
                          stateName: 'currentState',
                          cityLabelId:
                            values?.pub_talent_level === TalentLevel.HIGH_SCHOOL
                              ? 'EditListingDetailsForm.currentCityLabel'
                              : 'EditListingDetailsForm.cityLabel',
                          stateLabelId:
                            values?.pub_talent_level === TalentLevel.HIGH_SCHOOL
                              ? 'EditListingDetailsForm.currentStateLabel'
                              : 'EditListingDetailsForm.stateLabel',
                          cityValue: values?.currentCity || values.city,
                          stateValue: values?.currentState || values.state,
                          isReadOnly: values?.pub_talent_level === TalentLevel.COLLEGIATE,
                        })}
                      </div>
                    )}

                    <div className={css.manualLocationFields}>
                      {renderCityStateFields({
                        cityId: 'homeTownCity',
                        stateId: 'hometownState',
                        cityName: 'homeTownCity',
                        stateName: 'hometownState',
                        cityLabelId: 'EditListingDetailsForm.hometownCityLabel',
                        stateLabelId: 'EditListingDetailsForm.hometownStateLabel',
                      })}
                    </div>
                  </>
                )}

                {pub_talent_level === TalentLevel.HIGH_SCHOOL && (
                  <FieldTextInput
                    id="highSchoolName"
                    name="highSchoolName"
                    type="text"
                    className={css.customField}
                    label={intl.formatMessage({ id: 'EditListingDetailsForm.highSchoolNameLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'EditListingDetailsForm.highSchoolNamePlaceholder',
                    })}
                  />
                )}

                {renderAgencyField()}
              </>
            )
          ) : (
            <>
              <div className={css.manualLocationFields}>
                {renderCityStateFields({
                  cityId: 'currentCity',
                  stateId: 'currentState',
                  cityName: 'currentCity',
                  stateName: 'currentState',
                  cityLabelId: 'EditListingDetailsForm.currentCityLabel',
                  stateLabelId: 'EditListingDetailsForm.currentStateLabel',
                })}
              </div>

              <div className={css.manualLocationFields}>
                {renderCityStateFields({
                  cityId: 'homeTownCity',
                  stateId: 'hometownState',
                  cityName: 'homeTownCity',
                  stateName: 'hometownState',
                  cityLabelId: 'EditListingDetailsForm.hometownCityLabel',
                  stateLabelId: 'EditListingDetailsForm.hometownStateLabel',
                })}
              </div>

              {renderAgencyField()}
            </>
          )}

          {locationSectionsFieldProps
            .filter(field =>
              isEligibleForSchoolAndConference ? SCHOOL_FIELDS.includes(field.key) : false
            )
            .map(fieldProps => (
              <CustomExtendedDataField
                key={fieldProps.key}
                {...fieldProps}
                formId={formId}
                values={values}
                currentStep={currentStep}
                form={form}
              />
            ))}
        </div>
      </div>
    );
  }
);

export default RenderLocationSections;
